import { cn } from "@lib/cn";
import type { PickerVariable } from "../types";
import { VariableRow } from "./variable-row";

interface Props {
  variables: PickerVariable[];
  search: string;
  onInsert?: (variable: PickerVariable) => void;
  isLoading: boolean;
  isUninitialized: boolean;
  /** Id of the row to render in its highlighted state (e.g. keyboard nav). */
  highlightedId?: string;
}

/** Case-insensitive substring match on the variable's display name. Exported
 * so other surfaces (e.g. the inline `{{` typeahead) filter identically
 * without re-implementing this rule. */
export const filterVariables = (
  variables: PickerVariable[],
  search: string,
): PickerVariable[] => {
  const query = search.trim().toLowerCase();
  return query
    ? variables.filter((v) => v.name.toLowerCase().includes(query))
    : variables;
};

// Varied line widths so the placeholder rows read as a natural list.
const SKELETON_WIDTHS = [
  "tw-w-28",
  "tw-w-20",
  "tw-w-32",
  "tw-w-24",
  "tw-w-16",
  "tw-w-36",
];

/** Flat variable list (Fathom, Custom) — no categories / hover flyouts. */
export const VariableList = ({
  variables,
  search,
  onInsert,
  isLoading,
  isUninitialized,
  highlightedId,
}: Props) => {
  const matches = filterVariables(variables, search);

  // Initial load (no rows yet): show skeleton placeholders. A background
  // refetch that already has rows keeps showing them instead of flashing.
  if ((isLoading || isUninitialized) && matches.length === 0) {
    return (
      <div
        className="tw-flex tw-flex-col tw-gap-0.5"
        aria-busy="true"
        aria-live="polite"
      >
        {SKELETON_WIDTHS.map((width) => (
          <div
            key={width}
            className="tw-flex tw-h-10 tw-w-full tw-items-center tw-gap-3 tw-px-2"
          >
            <div className="tw-size-4 tw-shrink-0 tw-animate-pulse tw-rounded tw-bg-blue-100" />
            <div
              className={cn(
                "tw-h-3 tw-animate-pulse tw-rounded-full tw-bg-blue-100",
                width,
              )}
            />
          </div>
        ))}
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <p className="tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-blue-700">
        No variables found
      </p>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      {matches.map((v) => (
        <VariableRow
          key={v.id}
          variable={v}
          onInsert={onInsert}
          highlighted={highlightedId != null && v.id === highlightedId}
        />
      ))}
    </div>
  );
};
